/**
 * 연결별 동시 접속 한도 — 한 DB 에 동시에 붙는 태스크 수를 묶는다.
 *
 * 왜 필요한가: 연결선으로 잇지 않은 노드는 동시에 실행된다. 그 노드들이 같은 DB 를 쓰면 접속이 한꺼번에
 * 몰려 다른 업무가 접속하지 못할 수 있다. Airflow 의 전체 동시 실행 수 제한은 태스크 수 제한이지 특정
 * DB 에 대한 제한이 아니라서, 워크플로가 여러 개면 그 DB 에 붙는 수를 묶지 못한다.
 *
 * 여기서 정한 값은 Airflow 풀 슬롯 수가 되고, 배포할 때 그 연결을 쓰는 태스크에 자동으로 물린다.
 * 비워 두면 한도가 없다(예전과 같은 동작).
 *
 * 관리자에게는 연결마다 **사용 프로젝트**도 보인다 — 정해 두면 그 프로젝트의 워크플로만 그 연결을 쓴다
 * (서버 services/conn_scope, docs/RELEASE_SECURITY_REVIEW.md S14). 비워 두면 모든 프로젝트.
 * 둘 다 서버 전체 설정이라 관리자만 바꾼다 — 다른 사람에게는 보기만.
 */
import React, { useEffect, useState } from 'react';
import { BE1_API, be1Fetch } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { showAlert } from '../common/AppDialog';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../services/errorText';
import { useCapabilities } from '../../services/capabilities';

interface LimitRow {
  connection: string;
  limit: number;
  pool: string;
  poolExists: boolean;
  poolSlots: number | null;
  running: number | null;
  queued: number | null;
}

interface Props { onClose: () => void }

const ConnectionLimitsDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [conns, setConns] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, LimitRow>>({});
  const [edit, setEdit] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const caps = useCapabilities();
  const admin = !!caps?.admin;
  // 사용 프로젝트 (관리자) — 서버 값과 고친 값, 펼친 연결
  const [projects, setProjects] = useState<{ id: string; name: string; owner?: string }[]>([]);
  const [scopes, setScopes] = useState<Record<string, string[]>>({});
  const [scopeEdit, setScopeEdit] = useState<Record<string, string[]>>({});
  const [openScope, setOpenScope] = useState<string | null>(null);

  const loadScopes = async () => {
    const r = await be1Fetch('/api/connections/scopes');
    if (!r.ok) return;
    const j = await r.json();
    setProjects(j.projects || []);
    setScopes(j.scopes || {});
    setScopeEdit(j.scopes || {});
  };
  useEffect(() => { if (admin) loadScopes().catch(() => {}); }, [admin]);
  const sameSet = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((x) => b.includes(x));
  const toggleScope = (conn: string, pid: string) => setScopeEdit((p) => {
    const cur = p[conn] || [];
    return { ...p, [conn]: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] };
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [cl, ll] = await Promise.all([
        fetch(`${BE1_API}/api/connections/`).then((r) => r.json()),
        be1Fetch('/api/connections/limits').then((r) => r.json()),
      ]);
      const ids: string[] = (cl || []).map((c: any) => c.connection_id || c.conn_id).filter(Boolean);
      const byId: Record<string, LimitRow> = {};
      (ll.items || []).forEach((i: LimitRow) => { byId[i.connection] = i; });
      setConns(ids);
      setRows(byId);
      setEdit(Object.fromEntries(ids.map((id) => [id, byId[id] ? String(byId[id].limit) : ''])));
    } catch (e: any) {
      setError(e?.message || t('connection:limits.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      // 빈 칸은 0 으로 보내 한도를 없앤다 — 서버가 그 연결을 목록에서 뺀다
      const limits: Record<string, number> = {};
      conns.forEach((id) => { limits[id] = Number(edit[id]) > 0 ? Math.floor(Number(edit[id])) : 0; });
      const res = await be1Fetch('/api/connections/limits', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ limits }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(errorText(body, res.statusText));
      // 사용 프로젝트 — 바뀐 연결만 저장
      for (const id of conns) {
        if (sameSet(scopes[id], scopeEdit[id])) continue;
        const r = await be1Fetch(`/api/connections/scopes/${encodeURIComponent(id)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ projects: scopeEdit[id] || [] }),
        });
        if (!r.ok) throw new Error(errorText(await r.json().catch(() => ({})), r.statusText));
      }
      if (admin) await loadScopes();
      const failed = (body.pools || []).filter((p: any) => !p.ok);
      if (failed.length) {
        showAlert(t('connection:limits.partialFailed', { list: failed.map((p: any) => `· ${p.connection}: ${p.error}`).join('\n') }),
          { title: t('connection:limits.title'), tone: 'warning' });
      }
      await load();
    } catch (e: any) {
      showAlert(t('connection:limits.saveFailed', { message: e?.message || '' }), { title: t('connection:limits.title'), tone: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50 whitespace-normal text-left"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-[720px] max-w-[95vw] max-h-[85vh] bg-white rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
          <div className="font-semibold text-gray-900">{t('connection:limits.heading')}</div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label={t('connection:limits.close')}>✕</button>
        </div>

        <div className="px-4 pt-3 text-xs text-gray-500">
          {t('connection:limits.intro')} <b>{t('connection:limits.introBold')}</b>{t('connection:limits.introTail')}
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
          {loading && <div className="text-sm text-gray-500">{t('connection:limits.reading')}</div>}
          {error && <div className="text-sm text-red-600">{error}</div>}
          {!loading && !error && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <th className="py-1.5">{t('connection:limits.colConnection')}</th>
                  <th className="py-1.5 w-28">{t('connection:limits.colMax')}</th>
                  <th className="py-1.5 w-40">{t('connection:limits.colUsage')}</th>
                  {admin && <th className="py-1.5 w-36">{t('connection:scope.col')}</th>}
                </tr>
              </thead>
              <tbody>
                {conns.map((id) => {
                  const r = rows[id];
                  const sc = scopeEdit[id] || [];
                  return (
                    <React.Fragment key={id}>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 font-medium text-gray-800">{id}</td>
                      <td className="py-1.5">
                        <input
                          type="number" min={0} step={1}
                          value={edit[id] ?? ''}
                          onChange={(e) => setEdit((p) => ({ ...p, [id]: e.target.value }))}
                          placeholder={t('connection:limits.none')}
                          disabled={!admin}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="py-1.5 text-xs text-gray-500">
                        {r?.poolExists
                          ? t('connection:limits.usage', { running: r.running ?? 0, queued: r.queued ?? 0, slots: r.poolSlots ?? '-' })
                          : t('connection:limits.noLimit')}
                      </td>
                      {admin && (
                        <td className="py-1.5">
                          <button type="button" onClick={() => setOpenScope((o) => (o === id ? null : id))}
                            className={`text-xs px-2 py-0.5 rounded border ${sc.length ? 'border-amber-400 text-amber-800 bg-amber-50' : 'border-gray-300 text-gray-600'} hover:bg-gray-100`}>
                            {sc.length ? t('connection:scope.count', { n: sc.length }) : t('connection:scope.all')} {openScope === id ? '▴' : '▾'}
                          </button>
                        </td>
                      )}
                    </tr>
                    {admin && openScope === id && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={4} className="px-2 py-2">
                          <div className="text-[0.6875rem] text-gray-500 mb-1">{t('connection:scope.hint')}</div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 max-h-48 overflow-y-auto">
                            {projects.map((p) => (
                              <label key={p.id} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                                <input type="checkbox" checked={sc.includes(p.id)} onChange={() => toggleScope(id, p.id)} />
                                <span className="truncate">{p.name}</span>
                                {p.owner && <span className="text-gray-400 truncate">· {p.owner}</span>}
                              </label>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
                {conns.length === 0 && (
                  <tr><td colSpan={admin ? 4 : 3} className="py-6 text-center text-gray-500">{t('connection:limits.empty')}</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 px-4 py-2.5 border-t border-gray-200 bg-gray-50 shrink-0">
          <button type="button" onClick={load} disabled={loading || saving}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">{t('connection:limits.refresh')}</button>
          {caps && !admin && <span className="mr-auto text-xs text-amber-700">{t('connection:scope.adminOnly')}</span>}
          <button type="button" onClick={save} disabled={loading || saving || !admin}
            className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300">
            {saving ? t('connection:limits.saving') : t('connection:limits.save')}
          </button>
          <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-100">{t('connection:limits.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default ConnectionLimitsDialog;
